/**
 * case-ui.ts — saving to the server and opening a stored case (P2/US-214).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * OPENING A STORED CASE RENDERS THE SNAPSHOT. IT DOES NOT CALCULATE.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * D10, and it is the reason the table exists. `GET /v1/cases/:id` returns what the tool said on the
 * day the case was saved, and this file puts *that* on screen — including into `DERNIER`, the map
 * the note de calcul prints from. So a note printed from a stored case is the stored result, and
 * `note.test.ts`'s byte-identical comparison keeps passing without the renderer knowing anything
 * about storage.
 *
 * The inputs are written into the form as well, so the case is editable — but the moment a field
 * changes, the normal debounce fires a fresh calculation and the panel becomes a live result again.
 * That is correct: the user has changed the input, so it is no longer that stored case. What must
 * never happen is the reverse — opening a case and silently seeing today's numbers under the date it
 * was saved.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * TWO SAVE BUTTONS, AND THE OLD ONE KEEPS ITS NAME
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * US-214 asks for "Enregistrer" to save to the current project, and — one criterion later — for
 * local JSON save and open to keep working unchanged. Both cannot be the same control, and the
 * shell cannot be edited: `index.html`'s body is byte-identical to oracle lines 126–201 and
 * `shell.test.ts` asserts it.
 *
 * So the oracle's `#save` keeps its behaviour and its label ("Enregistrer le cas" → a `.json`
 * download), and the server save is a **new control, injected at runtime**, next to it. That is
 * also the better product answer: the local file is how a user gets their data *out*, and during a
 * pilot that reassurance matters more than a tidy button count.
 */

import type { CaseResponse, SavedModule } from '@ronceray/contract';

import { escapeHtml } from './dom';
import { shortDate } from './projects-ui';

/**
 * The default title for a save: the element label the engineer typed.
 *
 * The same field the local download uses for its filename, for the same reason — it is the only
 * name in the form that a human chose. An untitled case is allowed; a case titled `lamine-3` is
 * not, because a machine-made name is worse than none when a list is being scanned.
 */
export function defaultTitle(input: Record<string, unknown>): string | null {
  const repere = input['repere'];
  return typeof repere === 'string' && repere.trim() !== '' ? repere.trim().slice(0, 200) : null;
}

/** The strip that says which stored case is on screen, and when it was computed. */
export function caseHeaderHTML(stored: CaseResponse): string {
  const title = stored.title ?? '(sans titre)';
  return (
    '<div class="p2-openbar" id="p2-openbar">' +
    `<span>Cas enregistre : <strong>${escapeHtml(title)}</strong> ` +
    `&mdash; ${shortDate(stored.createdAt)}</span>` +
    '<span><button type="button" class="mini" id="p2-recalc">Recalculer</button> ' +
    '<button type="button" class="mini" id="p2-close-case">Fermer</button></span>' +
    '</div>'
  );
}

/** The control the injected chrome carries. Separate from the oracle's `#save`; see the header. */
export function saveControlHTML(projectName: string | undefined): string {
  const target =
    projectName === undefined
      ? 'Choisissez un projet pour enregistrer'
      : `Projet : ${escapeHtml(projectName)}`;

  return (
    `<span class="p2-project" id="p2-current-project">${target}</span>` +
    '<button type="button" id="p2-save-server"' +
    `${projectName === undefined ? ' disabled' : ''}>Enregistrer dans le projet</button>` +
    '<button type="button" id="p2-browse">Projets</button>'
  );
}

/** What a save attempt reports back. Short, because it is shown in the chrome, not in a dialog. */
export function saveOutcomeMessage(result: { ok: boolean; kind?: string }): string {
  if (result.ok) return 'Cas enregistre.';
  if (result.kind === 'invalid') return 'Saisie incomplete : le cas n a pas ete enregistre.';
  if (result.kind === 'auth') return 'Session expiree. Reconnectez-vous.';
  if (result.kind === 'unknown') return 'Projet introuvable.';
  return 'Le cas n a pas pu etre enregistre.';
}

/* ================================================================================================
 * The controller
 * ============================================================================================== */

export interface CaseDeps {
  readonly fetchCase: (
    caseId: string,
  ) => Promise<{ ok: boolean; value?: CaseResponse; kind?: string }>;
  readonly saveCase: (
    projectId: string,
    body: { module: SavedModule; title: string | null; inputs: Record<string, unknown> },
  ) => Promise<{ ok: boolean; value?: CaseResponse; kind?: string }>;
  /** Fill a module's form from stored inputs. `ecrire` in the application. */
  readonly writeForm: (module: SavedModule, inputs: Record<string, unknown>) => void;
  /** Read a module's form. `lire` in the application. */
  readonly readForm: (module: SavedModule) => Record<string, unknown>;
  /**
   * Put a STORED snapshot on screen and into `DERNIER`, so the note prints from it.
   *
   * A separate dependency from "calculate", deliberately: there is no code path here that could
   * accidentally recompute, because this controller has no way to.
   */
  readonly showStored: (stored: CaseResponse) => void;
  readonly showModule: (module: SavedModule) => void;
  readonly setMessage: (text: string) => void;
}

export function caseView(deps: CaseDeps) {
  return {
    /** Open a stored case: fill the form, render the snapshot, switch to its module. */
    async open(caseId: string): Promise<CaseResponse | undefined> {
      const fetched = await deps.fetchCase(caseId);

      if (!fetched.ok || fetched.value === undefined) {
        deps.setMessage(
          fetched.kind === 'unknown' ? 'Ce cas est introuvable.' : 'Ce cas n a pas pu etre ouvert.',
        );
        return undefined;
      }

      const stored = fetched.value;
      deps.writeForm(stored.module, stored.inputs);
      deps.showModule(stored.module);
      // Order matters: the module must be visible before the snapshot is painted, or the panel is
      // written into a hidden pane and the user sees the previous module's result.
      deps.showStored(stored);
      return stored;
    },

    /** Save the active module's form to a project. The server recomputes; we send inputs only. */
    async save(projectId: string, module: SavedModule): Promise<CaseResponse | undefined> {
      const inputs = deps.readForm(module);
      const saved = await deps.saveCase(projectId, {
        module,
        title: defaultTitle(inputs),
        inputs,
      });

      deps.setMessage(saveOutcomeMessage(saved));
      return saved.ok ? saved.value : undefined;
    },
  };
}
